import {
  DfpApi,
  ProductDetails,
  ProductListResponse,
  MintSummaryResponse,
  MintProductResults,
  StandardComplianceResponse,
  StandardsAdoptionResponse,
  ProductsComplianceResponse,
  StandardDetails,
  ProductStandardDetails,
  StandardComplianceSummaryListResponse,
  CommonPlatformOverallSummary,
  PlatformIntegrationSummary,
  ProductPlatformIntegration,
  TechnicalDebtDashboardResponse,
  TechnicalDebtSummaryResponse,
  TechnicalDebtTrendSummaryResponse,
  TechnicalDebtDetailsResponse,
  TechnicalDebtTrendResponse,
  TechnicalDebtRecommendationsResponse,
  ProductTechnicalDebt,
  TechnicalDebtItem,
  TechnicalDebtSolution,
  TechnicalDebtProjectDetailsResponse,
  CreateTechnicalDebtItemPayload,
  parseErrorResponse,
  isNetworkError,
  createErrorFromStatus,
  TechStackAnalyticsResponse,
  TechStackSecuritySummaryResponse,
  TechStackBackendRepoParams,
  TechStackBackendListResponse,
  TechStackBackendRepoDetail,
  TechStackFrontendRepoParams,
  TechStackFrontendListResponse,
  TechStackFrontendRepoDetail,
  TechStackFrameworksResponse,
  TechStackDatabasesResponse,
  TechStackTechnicalDebtResponse,
  TechStackProductEolResponse,
  ProductMasterBulkExportResponse,
  SecurityScanIssueSummary,
  SecurityScanIssueListParams,
  SecurityScanIssueListResponse,
  SecurityScanIssueDetail,
  SecurityScanIssueDivision,
  SecurityScanIssueStatusUpdateRequest,
  SecurityScanIssueItem,
  SecurityScanIssueAnalytics,
  SecurityScanIssueSyncResponse,
  SecurityScanIssueReviewRequest,
  SecurityScanIssueTrend,
  SecurityScanIssueTrendGranularity,
  SecurityScanIssueFacets,
  ArchHealthSyncResponse,
} from '@internal/plugin-dfp-common';
import { DiscoveryApi, FetchApi, IdentityApi } from '@backstage/core-plugin-api';

// ---------------------------------------------------------------------------
// Tech Stack API response transformers
// ---------------------------------------------------------------------------

const LANG_MAP: Record<string, string> = {
  GO: 'Go', KOTLIN: 'Kotlin', JAVA: 'Java',
  TYPESCRIPT: 'TypeScript', JAVASCRIPT: 'JavaScript',
};
function normLang(l: string): string {
  return LANG_MAP[l?.toUpperCase()] ?? l;
}

function transformBackendRepo(r: any): any {
  const sa = r.security_and_analysis ?? {};
  const std = r.standards_adoption ?? {};
  return {
    repository: r.repository,
    repository_url: r.repository_url,
    product_code: r.product_code,
    language: normLang(r.language),
    go_version: r.go_version,
    kotlin_version: r.kotlin_version,
    java_version: r.java_version,
    frameworks: r.frameworks ?? [],
    database_type: r.database_type,
    database_migration_info: r.database_migration_info,
    communication_protocol: r.communication_protocol,
    logging_libraries: r.logging_libraries,
    messaging_libraries: r.messaging_libraries,
    cache_libraries: r.cache_libraries ?? [],
    test_framework: r.test_framework ?? [],
    has_codeowners: std.has_codeowners ?? r.has_codeowners ?? false,
    renovate_enabled: sa.renovate_usage ?? r.renovate_enabled ?? false,
    using_mf_common: !!(std.using_mf_common_go || std.using_mf_common_kotlin || r.using_mf_common),
    sbom: r.sbom ? { total_packages: r.sbom.total_packages } : undefined,
    pushed_at: r.pushed_at,
    // detail fields pass-through
    database_library: r.database_library,
    database_connector: r.database_connector,
    build_tool: r.build_tool?.type ?? r.build_tool,
    go_modules: r.go_modules,
    dependabot_enabled: r.security_and_analysis?.dependabot_usage ?? r.dependabot_enabled ?? false,
    use_logging_standard: r.standards_adoption?.use_logging_standard_library ?? r.use_logging_standard ?? false,
    technical_debt_indicator: r.technical_debt_indicator,
  };
}

function transformFrontendRepo(r: any): any {
  const std = r.standards_adoption ?? {};
  return {
    repository: r.repository,
    repository_url: r.repository_url,
    product_code: r.product_code,
    language: normLang(r.language),
    typescript_version: r.typescript_version,
    nodejs_version: r.nodejs_version,
    frontend_framework: r.frontend_framework,
    package_manager: r.package_manager,
    build_tool: r.build_tool,
    test_framework: r.unit_test_tool ?? r.test_framework,
    has_mfui_components: !!(std.mfui_components ?? r.has_mfui_components),
    css_tech: r.css_tech,
    state_management: r.state_management,
    formatter: r.formatter,
    js_linter: r.js_linter,
    ui_catalog: r.ui_catalog,
    frontend_library: r.frontend_library,
    sbom: r.sbom ? { total_packages: r.sbom.total_packages } : undefined,
    pushed_at: r.pushed_at,
    // detail fields pass-through
    ui_library: r.ui_library,
    e2e_test_tool: r.e2e_test_tool,
    technical_debt_indicator: r.technical_debt_indicator,
  };
}

export class DfpApiClient implements DfpApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly identityApi?: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    identityApi?: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.identityApi = options.identityApi;
  }

  /**
   * Comma-separated list of the current user's Backstage group ownership
   * refs (e.g. `group:default/mfbc-cto-office,group:default/foo`). Used
   * by the manager-only review endpoint. Returns "" if the identity API
   * isn't wired or the user has no group memberships.
   */
  private async getForwardedGroupsHeader(): Promise<string> {
    if (!this.identityApi) return '';
    try {
      const identity = await this.identityApi.getBackstageIdentity();
      const refs = identity.ownershipEntityRefs ?? [];
      // Keep only group refs — user refs are irrelevant for role checks.
      return refs.filter(r => r.startsWith('group:')).join(',');
    } catch {
      return '';
    }
  }

  private async getBackendUrl() {
    return await this.discoveryApi.getBaseUrl('dfp');
  }

  async getHealth(): Promise<{ status: string }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/health`);
    return await response.json();
  }

  async getProductList(): Promise<ProductListResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/products`);
    if (!response.ok) { 
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText);
      throw new Error(parsedError ? JSON.stringify(parsedError) : errorText); 
    }
    return response.json();
  }

  async getProductDetails(productCode: string): Promise<ProductDetails> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/${productCode}`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getProductMasterDetails(productCode: string): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/product-master/${productCode}`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getProductMasterMaturity(productCode: string): Promise<{ maturityLevel: any }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/product-master/${productCode}/maturity`);
    if (!response.ok) {
      // Return null maturity data instead of throwing error
      return { maturityLevel: null };
    }
    return response.json();
  }

  async searchProductMaster(filters: any): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/product-master/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filters }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getProductMasterValueCatalog(): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/product-master/value-catalog`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getProductMasterHierarchyOptions(): Promise<{
    segments: string[];
    domains: string[];
    divisions: string[];
    divisionOptions: Array<{ name: string; title: string }>;
    tree: Array<{
      segmentName: string;
      segmentTitle: string;
      domains: Array<{
        domainName: string;
        domainTitle: string;
        divisions: Array<{ name: string; title: string }>;
      }>;
    }>;
  }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/product-master/hierarchy-options`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async exportProductMasterBulk(productCodes: string[]): Promise<ProductMasterBulkExportResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/product-master/export?responseStyle=bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ productCodes }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getMintSummary(): Promise<MintSummaryResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/maturity/mint`);
    if (!response.ok) { 
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError)); 
    }
    return response.json();
  }

  async getMintProductResults(productCode: string): Promise<MintProductResults> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/maturity/mint/${productCode}`);
    if (!response.ok) { 
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError)); 
    }
    return response.json();
  }

  async refreshProductData(productCode: string, sources: string[] = ['product_master']): Promise<any> {
    const url = await this.getBackendUrl();
    try {
      const response = await this.fetchApi.fetch(`${url}/${productCode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sources }),
      });
      
      if (!response.ok) { 
        const errorText = await response.text();
        const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
        throw new Error(JSON.stringify(parsedError));
      }
      
      return response.json();
    } catch (error) {
      // Handle network/connection errors
      if (isNetworkError(error)) {
        throw new Error(JSON.stringify({
          error: 'NETWORK_ERROR',
          message: 'Failed to refresh product data',
          details: 'Network error: Unable to connect to DFP service'
        }));
      }
      throw error;
    }
  }

  async getConfig(): Promise<{ lumos: { baseUrl: string } }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/config`);
    if (!response.ok) {
      const errorResponse = createErrorFromStatus(response.status, 'Failed to fetch config');
      throw new Error(JSON.stringify(errorResponse));
    }
    return response.json();
  }

  async checkLumosAccessibility(productCode: string): Promise<boolean> {
    try {
      const url = await this.getBackendUrl();
      const response = await this.fetchApi.fetch(`${url}/lumos-accessibility-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          productCode
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        return result.accessible === true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  async getStandards(): Promise<StandardComplianceResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/standard_compliance`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getStandardsAdoption(): Promise<StandardsAdoptionResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/standard_compliance/standards`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getProductsCompliance(): Promise<ProductsComplianceResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/standard_compliance/products`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getStandardDetails(standardCode: string): Promise<StandardDetails> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/standard_compliance/standards/${standardCode}`);
    if (!response.ok) { 
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError)); 
    }
    return response.json();
  }

  async updateStandardAdoptionStatus(productCode: string, standardAdoptionStatus: Record<string, string>): Promise<{ status: string; message: string }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/standard_compliance/products/${productCode}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ standard_adoption_status: standardAdoptionStatus }),
    });
    
    if (!response.ok) { 
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError)); 
    }
    return response.json();
  }

  async getProductStandardDetails(productCode: string): Promise<ProductStandardDetails> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/standard_compliance/products/${productCode}`);
    if (!response.ok) { 
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError)); 
    }
    return response.json();
  }

  async getStandardComplianceSummary(startDate?: string, endDate?: string): Promise<StandardComplianceSummaryListResponse> {
    const url = await this.getBackendUrl();
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('start_date', startDate);
    if (endDate) queryParams.append('end_date', endDate);
    const queryString = queryParams.toString();
    const fullUrl = queryString
      ? `${url}/v1/standard_compliance/summary?${queryString}`
      : `${url}/v1/standard_compliance/summary`;
    const response = await this.fetchApi.fetch(fullUrl);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getCommonPlatformIntegration(): Promise<CommonPlatformOverallSummary> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/platform_integration`);
    if (!response.ok) { 
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError)); 
    }
    return response.json();
  }

  async getPlatformIntegrationDetails(platformCode: string): Promise<PlatformIntegrationSummary> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/platform_integration/platforms/${platformCode}`);
    if (!response.ok) { 
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError)); 
    }
    return response.json();
  }

  async getProductPlatformIntegration(productCode: string): Promise<ProductPlatformIntegration> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/platform_integration/products/${productCode}`);
    if (!response.ok) { 
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError)); 
    }
    return response.json();
  }

  async updateProductPlatformIntegration(platformCode: string, platformIntegrationStatus: Record<string, string>): Promise<{ status: string; message: string }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/platform_integration/products/${platformCode}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ platform_integration_status: platformIntegrationStatus }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getAllPlatforms(): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/platforms`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getPlatformByCode(platformCode: string): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/platforms/${platformCode}`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async createOrUpdatePlatform(platformCode: string, payload: any): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/platforms/${platformCode}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getStandardByCode(standardCode: string): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/standards/${standardCode}`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getAllStandards(): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/standards`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async createOrUpdateStandard(standardCode: string, data: any): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/standards/${standardCode}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getStandardsBackground(): Promise<{ content: string }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/standards/background`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    const data = await response.json();
    return { content: data.content };
  }

  // Technical Debt API Methods

  async getTechnicalDebtDashboard(): Promise<TechnicalDebtDashboardResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getTechnicalDebtSummary(): Promise<TechnicalDebtSummaryResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/summary`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getTechnicalDebtTrendSummary(startDate?: string, endDate?: string): Promise<TechnicalDebtTrendSummaryResponse> {
    const url = await this.getBackendUrl();
    const queryParams = new URLSearchParams();
    if (startDate) {
      queryParams.append('start_date', startDate);
    }
    if (endDate) {
      queryParams.append('end_date', endDate);
    }
    const queryString = queryParams.toString();
    const fullUrl = `${url}/v1/technical_debt/summary${queryString ? `?${queryString}` : ''}`;
    const response = await this.fetchApi.fetch(fullUrl);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getTechnicalDebtDetails(debtCode: string): Promise<TechnicalDebtDetailsResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/items/${debtCode}`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getTechnicalDebtTrends(timePeriod: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' = 'MONTHLY'): Promise<TechnicalDebtTrendResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/trends?timePeriod=${timePeriod}`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getTechnicalDebtRecommendations(): Promise<TechnicalDebtRecommendationsResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/recommendations`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getProductTechnicalDebt(productCode: string): Promise<ProductTechnicalDebt> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/products/${productCode}`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getTechnicalDebtProjectDetails(projectKey: string): Promise<TechnicalDebtProjectDetailsResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/projects/${projectKey}`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async updateTechnicalDebtItem(debtCode: string, payload: Partial<TechnicalDebtItem> | CreateTechnicalDebtItemPayload): Promise<{ status: string; message: string }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/items/${debtCode}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async createTechnicalDebtSolution(debtCode: string, solution: Omit<TechnicalDebtSolution, 'solution_id'>): Promise<{ status: string; solution_id: string }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/items/${debtCode}/solutions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(solution),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async createTechnicalDebtItem(payload: CreateTechnicalDebtItemPayload): Promise<{ status: string; debt_item_id: string }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/items`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getTechnicalDebtItems(params?: {
    category?: string;
    severity?: string;
    status?: string;
    source?: string;
    project_key?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const url = await this.getBackendUrl();
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const fullUrl = `${url}/v1/technical_debt/items${queryString ? `?${queryString}` : ''}`;

    const response = await this.fetchApi.fetch(fullUrl);

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }

    return response.json();
  }

  async createNewTechnicalDebtSolution(payload: any): Promise<{ id: string; solution_id: string; debt_item_id: string }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/solutions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getTechnicalDebtSolutions(params?: {
    solution_id?: string;
    debt_item_id?: string;
    project_key?: string;
    status?: string;
    priority?: string;
    source?: string;
    category?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const url = await this.getBackendUrl();
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const fullUrl = `${url}/v1/technical_debt/solutions${queryString ? `?${queryString}` : ''}`;

    const response = await this.fetchApi.fetch(fullUrl);

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getTechnicalDebtSolutionDetails(solutionId: string): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/solutions/${solutionId}`);

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async updateTechnicalDebtSolution(solutionId: string, payload: any): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/technical_debt/solutions/${solutionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  // ============================================================================
  // PRODUCT MASTER EDIT REQUEST METHODS
  // ============================================================================

  async dispatchProductEditRequest(payload: {
    productCode: string;
    changes: any;
    reason: string;
    requestedBy: string;
    requestType?: 'edit' | 'create';
    baseBranch?: string;
    dryRun?: boolean;
  }): Promise<{
    workflowId: string;
    ref: string;
    dispatchedAt: string;
    status: 'queued';
    mode: 'workflow_dispatch';
    note?: string;
  }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/product-master/edit-request/dispatch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getProductEditRequestStatus(prNumber: number): Promise<{
    requestId: string;
    productCode: string;
    prNumber: number;
    prUrl: string;
    status: 'pending' | 'approved' | 'rejected' | 'merged' | 'closed';
    createdAt: string;
    updatedAt: string;
    approvedBy?: string;
    rejectedBy?: string;
    reason: string;
    requestedBy: string;
  }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/product-master/edit-request/${prNumber}`);

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async listProductEditRequests(filters?: {
    status?: 'open' | 'closed' | 'all';
    productCode?: string;
    includeReviewStatus?: boolean;
  }): Promise<{
    requests: Array<{
      requestId: string;
      productCode: string;
      prNumber: number;
      prUrl: string;
      status: 'pending' | 'approved' | 'rejected' | 'merged' | 'closed';
      createdAt: string;
      updatedAt: string;
      reason: string;
      requestedBy: string;
    }>;
    totalCount: number;
    filters: {
      status: string;
      productCode: string;
      includeReviewStatus?: boolean;
    };
    /** Present when the backend could not use the GitHub App but returned an empty list instead of 500. */
    degraded?: boolean;
    /** e.g. GITHUB_APP_OR_INSTALL_UNAVAILABLE | GITHUB_PRODUCT_MASTER_REPO_INACCESSIBLE */
    degradedReason?: string;
  }> {
    const url = await this.getBackendUrl();
    const queryParams = new URLSearchParams();

    if (filters?.status) {
      queryParams.append('status', filters.status);
    }
    if (filters?.productCode) {
      queryParams.append('productCode', filters.productCode);
    }
    if (filters?.includeReviewStatus !== undefined) {
      queryParams.append('includeReviewStatus', String(filters.includeReviewStatus));
    }

    const queryString = queryParams.toString();
    const querySuffix = queryString.length > 0 ? `?${queryString}` : '';
    const fullUrl = `${url}/product-master/edit-requests${querySuffix}`;

    const response = await this.fetchApi.fetch(fullUrl);

    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getTechStackAnalytics(
    params?: { group_by?: string; repository_type?: string },
  ): Promise<TechStackAnalyticsResponse> {
    const url = await this.getBackendUrl();
    const qs = params ? new URLSearchParams(params as any).toString() : '';
    const queryString = qs ? `?${qs}` : '';
    const response = await this.fetchApi.fetch(`${url}/tech-stack/analytics${queryString}`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(parseErrorResponse(errorText) ? JSON.stringify(parseErrorResponse(errorText)) : errorText);
    }
    const raw = await response.json();
    const total = raw.total_repositories ?? raw.total ?? 0;
    const groups = (raw.data ?? raw.groups ?? []).map((g: any) => ({
      key: normLang(g.label ?? g.key),
      count: g.count,
      percentage: total > 0 ? Math.round((g.count / total) * 100) : 0,
    }));
    return { group_by: raw.group_by ?? 'language', groups, total };
  }

  async getTechStackSecuritySummary(): Promise<TechStackSecuritySummaryResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/tech-stack/security/summary`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(parseErrorResponse(errorText) ? JSON.stringify(parseErrorResponse(errorText)) : errorText);
    }
    const raw = await response.json();
    const secTools = raw.security_tools_adoption ?? {};
    const stdAdoption = raw.standards_adoption ?? {};
    return {
      renovate_enabled_percent: secTools.renovate?.adoption_percentage ?? raw.renovate_enabled_percent ?? 0,
      codeowners_percent: stdAdoption.codeowners?.adoption_percentage ?? raw.codeowners_percent ?? 0,
      mf_common_percent: stdAdoption.mf_common_libraries?.adoption_percentage ?? raw.mf_common_percent ?? 0,
      eol_count: raw.technical_debt?.eol_versions?.repositories_affected ?? raw.technical_debt?.eol_versions?.total ?? raw.eol_count ?? 0,
      license_violations: raw.license_compliance?.repositories_with_problematic_licenses ?? raw.license_violations ?? 0,
    };
  }

  async getTechStackBackendRepos(
    params?: TechStackBackendRepoParams,
  ): Promise<TechStackBackendListResponse> {
    const url = await this.getBackendUrl();
    const qs = params ? new URLSearchParams(params as any).toString() : '';
    const queryString = qs ? `?${qs}` : '';
    const response = await this.fetchApi.fetch(`${url}/tech-stack/backend/repositories${queryString}`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(parseErrorResponse(errorText) ? JSON.stringify(parseErrorResponse(errorText)) : errorText);
    }
    const raw = await response.json();
    return {
      repositories: (raw.repositories ?? []).map(transformBackendRepo),
      total: raw.total ?? 0,
      limit: raw.limit ?? 50,
      offset: raw.offset ?? 0,
    };
  }

  async getTechStackBackendRepoDetail(repoName: string): Promise<TechStackBackendRepoDetail> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(
      `${url}/tech-stack/backend/repositories/${encodeURIComponent(repoName)}`,
    );
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    const raw = await response.json();
    return transformBackendRepo(raw);
  }

  async getTechStackFrontendRepos(
    params?: TechStackFrontendRepoParams,
  ): Promise<TechStackFrontendListResponse> {
    const url = await this.getBackendUrl();
    const qs = params ? new URLSearchParams(params as any).toString() : '';
    const queryString = qs ? `?${qs}` : '';
    const response = await this.fetchApi.fetch(`${url}/tech-stack/frontend/repositories${queryString}`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(parseErrorResponse(errorText) ? JSON.stringify(parseErrorResponse(errorText)) : errorText);
    }
    const raw = await response.json();
    return {
      repositories: (raw.repositories ?? []).map(transformFrontendRepo),
      total: raw.total ?? 0,
      limit: raw.limit ?? 50,
      offset: raw.offset ?? 0,
    };
  }

  async getTechStackFrontendRepoDetail(repoName: string): Promise<TechStackFrontendRepoDetail> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(
      `${url}/tech-stack/frontend/repositories/${encodeURIComponent(repoName)}`,
    );
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    const raw = await response.json();
    return transformFrontendRepo(raw);
  }

  async getTechStackFrameworks(): Promise<TechStackFrameworksResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/tech-stack/frameworks`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(parseErrorResponse(errorText) ? JSON.stringify(parseErrorResponse(errorText)) : errorText);
    }
    const raw = await response.json();
    const be = raw.backend_frameworks ?? {};
    const allFw = [
      ...(be.web_frameworks ?? []),
      ...(be.di_frameworks ?? []),
      ...(raw.frontend_frameworks ?? []),
      ...(raw.frameworks ?? []),
    ];
    const seen = new Set<string>();
    const frameworks = allFw
      .filter((f: any) => {
        const key = f.name;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .map((f: any) => ({ name: f.name, language: f.language ?? '', repo_count: f.usage_count ?? f.repo_count ?? 0, repositories: f.repositories ?? [] }));
    return { frameworks };
  }

  async getTechStackDatabases(): Promise<TechStackDatabasesResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/tech-stack/databases`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(parseErrorResponse(errorText) ? JSON.stringify(parseErrorResponse(errorText)) : errorText);
    }
    const raw = await response.json();
    const databases = (raw.database_types ?? raw.databases ?? []).map((d: any) => ({
      type: d.type,
      repo_count: d.usage_count ?? d.repo_count ?? 0,
      repositories: d.repositories ?? [],
    }));
    return { databases };
  }

  async updateTechStackRepoProductCode(repoName: string, productCode: string, repoType: 'backend' | 'frontend'): Promise<{ status: string; message: string }> {
    const url = await this.getBackendUrl();
    const path = repoType === 'backend'
      ? `${url}/tech-stack/backend/repositories/${encodeURIComponent(repoName)}`
      : `${url}/tech-stack/frontend/repositories/${encodeURIComponent(repoName)}`;
    const response = await this.fetchApi.fetch(path, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_code: productCode }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(parseErrorResponse(errorText) ? JSON.stringify(parseErrorResponse(errorText)) : errorText);
    }
    return response.json();
  }

  async getTechStackTechnicalDebt(params: { debt_type: string; language?: string; product_code?: string; severity?: string }): Promise<TechStackTechnicalDebtResponse> {
    const url = await this.getBackendUrl();
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>).toString();
    const response = await this.fetchApi.fetch(`${url}/tech-stack/technical-debt?${qs}`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(parseErrorResponse(errorText) ? JSON.stringify(parseErrorResponse(errorText)) : errorText);
    }
    const raw = await response.json();
    return {
      repositories: raw.repositories ?? [],
      total: raw.total ?? 0,
    };
  }

  async getTechStackProductEol(productCode: string): Promise<TechStackProductEolResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(
      `${url}/tech-stack/eol/${encodeURIComponent(productCode)}`,
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(parseErrorResponse(errorText) ? JSON.stringify(parseErrorResponse(errorText)) : errorText);
    }
    const raw = await response.json();
    return {
      product_code: raw.product_code ?? productCode,
      eol_data: Array.isArray(raw.eol_data) ? raw.eol_data : [],
    };
  }

  async getArchHealthProducts(params?: {
    severity?: string;
    type?: string;
    min_score?: number;
    max_score?: number;
    search?: string;
    sort_by?: string;
    sort_order?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const url = await this.getBackendUrl();
    const qs = new URLSearchParams();
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.type) qs.set('type', params.type);
    if (params?.min_score !== undefined) qs.set('min_score', String(params.min_score));
    if (params?.max_score !== undefined) qs.set('max_score', String(params.max_score));
    if (params?.search) qs.set('search', params.search);
    if (params?.sort_by) qs.set('sort_by', params.sort_by);
    if (params?.sort_order) qs.set('sort_order', params.sort_order);
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    const qstr = qs.toString();
    const productsUrl = `${url}/v1/arch_healthcheck/products`;
    const response = await this.fetchApi.fetch(qstr ? `${productsUrl}?${qstr}` : productsUrl);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getArchHealthProductDetail(productCode: string): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/arch_healthcheck/products/${encodeURIComponent(productCode)}`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getArchHealthAssessmentDetail(assessmentId: string): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/arch_healthcheck/assessments/${encodeURIComponent(assessmentId)}`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async updateArchHealthAssessment(assessmentId: string, payload: { product_code: string }): Promise<{ id: string; product_code: string; previous_product_code: string }> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/arch_healthcheck/assessments/${encodeURIComponent(assessmentId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getArchHealthProductHistory(productCode: string, params?: { from_date?: string; to_date?: string }): Promise<any> {
    const url = await this.getBackendUrl();
    const qs = new URLSearchParams();
    if (params?.from_date) qs.set('from_date', params.from_date);
    if (params?.to_date) qs.set('to_date', params.to_date);
    const qstr = qs.toString();
    const encodedCode = encodeURIComponent(productCode);
    const historyUrl = `${url}/v1/arch_healthcheck/products/${encodedCode}/history`;
    const response = await this.fetchApi.fetch(qstr ? `${historyUrl}?${qstr}` : historyUrl);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getArchHealthSummary(): Promise<any> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/v1/arch_healthcheck/summary`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async getArchHealthFindings(params?: {
    product_code?: string;
    category?: string;
    severity?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const url = await this.getBackendUrl();
    const qs = new URLSearchParams();
    if (params?.product_code) qs.set('product_code', params.product_code);
    if (params?.category) qs.set('category', params.category);
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    const qstr = qs.toString();
    const findingsUrl = `${url}/v1/arch_healthcheck/findings`;
    const response = await this.fetchApi.fetch(qstr ? `${findingsUrl}?${qstr}` : findingsUrl);
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
    return response.json();
  }

  async submitArchHealthFindingFeedback(payload: { assessment_id: string; feedbacks: Array<{ finding_id: string; entries: Array<{ author: string; comment: string }> }> }): Promise<void> {
    const url = await this.getBackendUrl();
    const assessmentId = encodeURIComponent(payload.assessment_id);
    const response = await this.fetchApi.fetch(`${url}/v1/arch_healthcheck/assessments/${assessmentId}/feedbacks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
  }

  async putArchHealthFindingFeedbacks(productCode: string, findingId: string, feedbacks: Array<{ author: string; comment: string }>): Promise<void> {
    const url = await this.getBackendUrl();
    const encodedProduct = encodeURIComponent(productCode);
    const encodedFinding = encodeURIComponent(findingId);
    const response = await this.fetchApi.fetch(`${url}/v1/arch_healthcheck/findings/${encodedProduct}/${encodedFinding}/feedbacks`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ feedbacks }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
  }

  async resolveArchHealthFindingFeedback(productCode: string, findingId: string): Promise<void> {
    const url = await this.getBackendUrl();
    const encodedProduct = encodeURIComponent(productCode);
    const encodedFinding = encodeURIComponent(findingId);
    const response = await this.fetchApi.fetch(`${url}/v1/arch_healthcheck/findings/${encodedProduct}/${encodedFinding}/feedback_state`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ feedback_state: 'RESOLVED' }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      const parsedError = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsedError));
    }
  }

  // ── Security Scan Issues ─────────────────────────────────────────────────
  async getSecurityScanIssueSummary(): Promise<SecurityScanIssueSummary> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/security-scan-issues/summary`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    return response.json();
  }

  async getSecurityScanIssues(
    params?: SecurityScanIssueListParams,
  ): Promise<SecurityScanIssueListResponse> {
    const url = await this.getBackendUrl();
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
          // Repeat the key for each array entry — matches typical REST list-filter
          // convention and what the upstream consumes per the contract.
          value.forEach(v => {
            if (v !== undefined && v !== null && v !== '') {
              qs.append(key, String(v));
            }
          });
        } else {
          qs.append(key, String(value));
        }
      });
    }
    const qstr = qs.toString();
    const fullUrl = qstr
      ? `${url}/security-scan-issues?${qstr}`
      : `${url}/security-scan-issues`;
    const response = await this.fetchApi.fetch(fullUrl);
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    return response.json();
  }

  async getSecurityScanIssueDetail(id: string): Promise<SecurityScanIssueDetail> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(
      `${url}/security-scan-issues/${encodeURIComponent(id)}`,
    );
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    // The upstream wraps the row in `{ issue, status_history }`. Flatten
    // to the SecurityScanIssueDetail shape the UI expects (item fields at
    // the top level + status_history alongside).
    const raw = await response.json();
    if (raw && typeof raw === 'object' && 'issue' in raw && raw.issue) {
      return {
        ...raw.issue,
        status_history: raw.status_history ?? [],
      } as SecurityScanIssueDetail;
    }
    // Already flat (forward-compat with a future API change).
    return raw as SecurityScanIssueDetail;
  }

  async getSecurityScanIssueDivisions(): Promise<SecurityScanIssueDivision[]> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/security-scan-issues/divisions`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    const raw = await response.json();
    return Array.isArray(raw) ? raw : raw?.divisions ?? [];
  }

  async getSecurityScanIssueAnalytics(): Promise<SecurityScanIssueAnalytics> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/security-scan-issues/analytics`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    return response.json();
  }

  async getSecurityScanIssueFacets(): Promise<SecurityScanIssueFacets> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/security-scan-issues/facets`);
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    return response.json();
  }

  async getSecurityScanIssueTrend(
    granularity: SecurityScanIssueTrendGranularity = 'week',
  ): Promise<SecurityScanIssueTrend> {
    const url = await this.getBackendUrl();
    const qs = new URLSearchParams({ granularity }).toString();
    const response = await this.fetchApi.fetch(
      `${url}/security-scan-issues/trend?${qs}`,
    );
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    return response.json();
  }

  async triggerSecurityScanIssueSync(): Promise<SecurityScanIssueSyncResponse> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(`${url}/security-scan-issues/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    // 409 = conflict (a sync is already running). The body is still a
    // SecurityScanIssueSyncResponse with status='conflict' — parse it.
    if (response.status === 409) {
      return response.json();
    }
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    return response.json();
  }

  async updateSecurityScanIssueStatus(
    id: string,
    body: SecurityScanIssueStatusUpdateRequest,
  ): Promise<SecurityScanIssueItem> {
    const url = await this.getBackendUrl();
    const response = await this.fetchApi.fetch(
      `${url}/security-scan-issues/${encodeURIComponent(id)}/status`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    return response.json();
  }

  async submitSecurityScanIssueReview(
    id: string,
    body: SecurityScanIssueReviewRequest,
  ): Promise<SecurityScanIssueItem> {
    const url = await this.getBackendUrl();
    const groups = await this.getForwardedGroupsHeader();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (groups) headers['X-Forwarded-Groups'] = groups;

    const response = await this.fetchApi.fetch(
      `${url}/security-scan-issues/${encodeURIComponent(id)}/review`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      const errorText = await response.text();
      const parsed = parseErrorResponse(errorText) || createErrorFromStatus(response.status);
      throw new Error(JSON.stringify(parsed));
    }
    return response.json();
  }
  async triggerArchHealthSync(): Promise<ArchHealthSyncResponse> {
    const url = await this.getBackendUrl();
    try {
      const response = await this.fetchApi.fetch(`${url}/v1/arch_healthcheck/sync`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (!response.ok && response.status !== 409) {
        const parsedError = parseErrorResponse(JSON.stringify(result)) || createErrorFromStatus(response.status);
        throw new Error(JSON.stringify(parsedError));
      }
      return result;
    } catch (error) {
      if (isNetworkError(error)) {
        throw new Error(JSON.stringify({
          error: 'NETWORK_ERROR',
          message: 'Failed to trigger Architecture Healthcheck sync',
          details: 'Network error: Unable to connect to DFP service',
        }));
      }
      throw error;
    }
  }
}